// components/about/AboutHeroAlt.tsx

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const AboutHeroAlt: React.FC = () => {
  const highlights = [
    "Founded in 2020",
    "50,000+ Global Contributors",
    "10+ Countries",
    "$1M+ Paid to Experts",
  ];

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#0a0a0f]">
      {/* Background Effects */}
      <div className="absolute inset-0">
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(#8B52FF 1px, transparent 1px), linear-gradient(90deg, #8B52FF 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-[#8B52FF]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[600px] h-[600px] bg-[#E05A6A]/20 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B52FF]/20 to-[#E05A6A]/20 border border-[#8B52FF]/20">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8B52FF] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8B52FF]"></span>
                </span>
                <span className="text-sm font-medium text-white">
                  About Unimerg
                </span>
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6"
            >
              On a Mission to
              <span className="block mt-2 bg-gradient-to-r from-[#8B52FF] via-[#A855F7] to-[#E05A6A] bg-clip-text text-transparent">
                Democratize AI
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-gray-400 leading-relaxed mb-8"
            >
              Unimerg connects the world's brightest minds with opportunities to
              shape how AI understands language, culture, and human intent. We
              believe diverse perspectives create better AI for everyone.
            </motion.p>

            {/* Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 gap-3 mb-8"
            >
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#8B52FF] flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/careers">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-4 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white rounded-xl font-semibold text-lg shadow-xl shadow-[#8B52FF]/25 hover:shadow-2xl transition-all duration-300 flex items-center gap-2 group"
                >
                  Join Our Team
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative">
              {/* Main visual container */}
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#8B52FF]/20 to-[#E05A6A]/20 border border-white/10 p-8 lg:p-12">
                {/* Animated circles representing global community */}
                <div className="relative aspect-square">
                  {/* Center logo/icon */}
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-2xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center shadow-2xl shadow-[#8B52FF]/30 z-20"
                  >
                    <span className="text-4xl">🌐</span>
                  </motion.div>

                  {/* Orbiting elements */}
                  {[...Array(8)].map((_, i) => {
                    const angle = (i * 360) / 8;
                    const radius = 120;
                    return (
                      <motion.div
                        key={i}
                        animate={{
                          rotate: 360,
                        }}
                        transition={{
                          duration: 20 + i * 2,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="absolute top-1/2 left-1/2 w-full h-full"
                        style={{ transformOrigin: "center center" }}
                      >
                        <motion.div
                          initial={{ rotate: angle }}
                          className="absolute w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center"
                          style={{
                            top: `calc(50% - 20px - ${radius * Math.cos((angle * Math.PI) / 180)}px)`,
                            left: `calc(50% - 20px + ${radius * Math.sin((angle * Math.PI) / 180)}px)`,
                          }}
                        >
                          <span className="text-lg">
                            {
                              ["🇺🇸", "🇬🇧", "🇯🇵", "🇩🇪", "🇧🇷", "🇮🇳", "🇫🇷", "🇰🇷"][
                                i
                              ]
                            }
                          </span>
                        </motion.div>
                      </motion.div>
                    );
                  })}

                  {/* Outer ring */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] rounded-full border border-dashed border-white/20" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full border border-white/10" />
                </div>

                {/* Stats overlay cards */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute top-4 right-4 p-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20"
                >
                  <div className="text-2xl font-bold text-white">10+</div>
                  <div className="text-xs text-gray-400">Countries</div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute bottom-4 left-4 p-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20"
                >
                  <div className="text-2xl font-bold text-white">50K+</div>
                  <div className="text-xs text-gray-400">Contributors</div>
                </motion.div>
              </div>

              {/* Decorative glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#8B52FF]/20 to-[#E05A6A]/20 rounded-3xl blur-2xl -z-10" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0f] to-transparent" />
    </section>
  );
};

export default AboutHeroAlt;
